import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { defer, finalize, map, Observable, tap } from 'rxjs';

import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { MsgService } from '../../Shared/Services/msg.service';
import { NavigationLockService } from '../../Shared/Services/navigation-lock.service';
import { ApplicationDemandsService } from '../Services/application-demands.service';
import { GroupDemandsService } from '../Services/group-demands.service';
import {
  ApplicationDemandDetail,
  ApplicationDemandDetailResponse,
} from '../../../Interfaces/api/applications/demand-detail.interface';
import {
  GroupDemandDetail,
  GroupDemandDetailResponse,
} from '../../../Interfaces/api/groups/demand-detail.interface';
import {
  ApproveDemandPayload,
  ApproveDemandResponse,
  DemandActionData,
  RejectDemandPayload,
  RejectDemandResponse,
} from '../../../Interfaces/api/applications/approval-action.interface';
import { DemandSubjectType } from '../../../Enums/demand-subject-type.enum';

@Injectable()
export class MyApprovalDetailFacade {
  private readonly _appSvc = inject(ApplicationDemandsService);
  private readonly _groupSvc = inject(GroupDemandsService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _msg = inject(MsgService);
  private readonly _navLock = inject(NavigationLockService);

  private readonly _demandId = signal<string>('');
  private readonly _subjectType = signal<DemandSubjectType>(DemandSubjectType.Application);

  readonly demandId = computed(() => this._demandId());
  readonly subjectType = computed(() => this._subjectType());

  readonly applicationDemand = httpResource<ApplicationDemandDetailResponse>(() => {
    if (this._subjectType() !== DemandSubjectType.Application || !this._demandId()) {
      return undefined;
    }
    return this._appSvc.showForApprover(this._demandId());
  });

  readonly groupDemand = httpResource<GroupDemandDetailResponse>(() => {
    if (this._subjectType() !== DemandSubjectType.Group || !this._demandId()) {
      return undefined;
    }
    return this._groupSvc.showForApprover(this._demandId());
  });

  readonly loading = computed<boolean>(
    () => this.applicationDemand.isLoading() || this.groupDemand.isLoading(),
  );

  readonly demand = computed<ApplicationDemandDetail | GroupDemandDetail | null>(() => {
    if (this._subjectType() === DemandSubjectType.Application) {
      return this.applicationDemand.hasValue() ? this.applicationDemand.value().data : null;
    }
    return this.groupDemand.hasValue() ? this.groupDemand.value().data : null;
  });

  readonly notFound = computed<boolean>(() => {
    const err =
      this._subjectType() === DemandSubjectType.Application
        ? this.applicationDemand.error()
        : this.groupDemand.error();
    if (!err) return false;
    const status = (err as { status?: number }).status;
    return status === 404 || status === undefined;
  });

  readonly loadFailed = computed<boolean>(() => {
    const err =
      this._subjectType() === DemandSubjectType.Application
        ? this.applicationDemand.error()
        : this.groupDemand.error();
    return !!err && !this.notFound();
  });

  readonly canAct = computed<boolean>(() => this.demand()?.caller_can_act ?? false);

  setRoute(subjectType: DemandSubjectType, demandId: string): void {
    this._subjectType.set(subjectType);
    this._demandId.set(demandId);
  }

  reset(): void {
    this._demandId.set('');
  }

  reload(): void {
    if (!this._demandId()) return;
    if (this._subjectType() === DemandSubjectType.Application) {
      this.applicationDemand.reload();
    } else {
      this.groupDemand.reload();
    }
  }

  approve(comment?: string | null): Observable<DemandActionData> {
    const id = this._demandId();
    const isApp = this._subjectType() === DemandSubjectType.Application;
    const payload: ApproveDemandPayload = { comment: comment ?? null };
    const action$: Observable<ApproveDemandResponse> = isApp
      ? this._appSvc.approve(id, payload)
      : this._groupSvc.approve(id, payload);

    return defer(() => {
      this._navLock.lock();
      return action$;
    }).pipe(
      tap((response) => {
        if (isApp) {
          this.applicationDemand.reload();
        } else {
          this.groupDemand.reload();
        }
        this._catalogOperations.reload();
        this._msg.showSnackBar(response.message, { type: 'success' });
      }),
      finalize(() => this._navLock.unlock()),
      map((response) => response.data),
    );
  }

  reject(comment?: string | null): Observable<DemandActionData> {
    const id = this._demandId();
    const isApp = this._subjectType() === DemandSubjectType.Application;
    const payload: RejectDemandPayload = { comment: comment ?? null };
    const action$: Observable<RejectDemandResponse> = isApp
      ? this._appSvc.reject(id, payload)
      : this._groupSvc.reject(id, payload);

    return defer(() => {
      this._navLock.lock();
      return action$;
    }).pipe(
      tap((response) => {
        if (isApp) {
          this.applicationDemand.reload();
        } else {
          this.groupDemand.reload();
        }
        this._catalogOperations.reload();
        this._msg.showSnackBar(response.message, { type: 'success' });
      }),
      finalize(() => this._navLock.unlock()),
      map((response) => response.data),
    );
  }
}
